/**
 * Inicis Payment Repository Implementation
 * KG이니시스 결제 레포지토리 구현 (Data Layer)
 */

import type { PaymentRequest } from '../../domain/models/payments/Payment';
import type { PaymentRepository } from '../../domain/repositories/PaymentRepository';
import { createOrder } from '../../domains/orders/services/orderServices';
import { ApprovePayment } from '../../domains/payments/services/paymentService';
import baseUrl from '../../utils/baseUrl';

/**
 * KG이니시스 결제 레포지토리 구현
 */
export class InicisPaymentRepositoryImpl implements PaymentRepository {
  /**
   * 결제 요청
   * KG이니시스 모바일 결제창 호출
   */
  async requestPayment(request: PaymentRequest): Promise<void> {
    // 폼 생성
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = 'https://mobile.inicis.com/smart/payment/';
    form.acceptCharset = 'EUC-KR';

    // BASE64_ENCODE(SHA512(P_AMT+P_OID+P_TIMESTAMP+HashKey))
    const amount = request.price.toString();
    const orderId = request.oid;
    const timestamp = Date.now(); // 숫자로 유지 (Node.js 예제와 동일)
    const hashKey = 'FEC1469619096DA59963CC9E16C1BF6E';

    // 해시 생성 (timestamp를 문자열로 변환하여 전달)
    const hash = await this.generatePChkFake(amount, orderId, timestamp.toString(), hashKey);

    // 필수 파라미터 추가 (이니시스 샘플 기준)
    const params = {
      P_INI_PAYMENT: request.gopaymethod, // 지불수단 (CARD, BANK, VBANK, HPP, CARS)
      P_MID: request.mid, // 상점아이디
      P_OID: orderId, // 주문번호
      P_AMT: amount, // 결제금액
      P_GOODS: request.goodname, // 상품명
      P_UNAME: request.buyername, // 구매자명
      P_MOBILE: request.buyertel, // 구매자 전화번호
      P_EMAIL: request.buyeremail, // 구매자 이메일
      P_NEXT_URL: `${baseUrl()}/webhooks/v1/inicis/callback`, // 결과수신 URL (백엔드 API) - env 분기 처리
      P_CHARSET: 'utf8', // 인코딩
      P_RESERVED: 'below1000=Y&vbank_receipt=Y&centerCd=Y&amt_hash=Y&app_scheme=pocapan://', // 추가 옵션
      P_NOTI: orderId, // 가맹점 임의 데이터 (주문번호 전달)
      P_TIMESTAMP: timestamp.toString(), // 숫자를 문자열로 변환
      P_CHKFAKE: hash
    };

    // 폼에 파라미터 추가
    Object.entries(params).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });

    // 폼을 body에 추가하고 submit
    document.body.appendChild(form);
    form.submit();

    // 폼 제거
    document.body.removeChild(form);
  }

  /**
   * 결제 승인 수행
   * - URL 파라미터를 파싱하여 백엔드 승인 API 호출
   * - 성공 여부를 boolean으로 반환
   */
  async approvePaymentFromSearchParams(searchParams: URLSearchParams): Promise<boolean> {
    const paymentKey = searchParams.get('paymentKey');
    const orderId = searchParams.get('orderId');
    const amount = searchParams.get('amount');
    const requestUrl = searchParams.get('requestUrl');
    const status = searchParams.get('status');

    if (status !== '00') {
      return false;
    }

    if (!paymentKey || !orderId || !amount || !requestUrl) {
      return false;
    }

    const body = {
      payment: {
        payment_key: paymentKey,
        order_id: orderId,
        amount: Number(amount),
        provider: 'inicis' as const,
        request_url: requestUrl
      }
    };
    const response = await ApprovePayment(body);
    if (response?.paymentId) {
      return true;
    }
    return false;
  }

  /**
   * 주문번호 생성
   */
  async generateOrderId(productId: string): Promise<string> {
    const response = await createOrder({
      order: {
        items: [
          {
            product_id: productId,
            product_type: 'bettie_product' as const,
            quantity: 1
          }
        ]
      }
    });
    if (!response.orderId) {
      throw new Error('주문번호 생성에 실패했습니다');
    }
    return response.orderId;
  }

  /**
   * P_CHKFAKE 해시 생성
   * BASE64_ENCODE(SHA512(P_AMT+P_OID+P_TIMESTAMP+HashKey))
   */
  private async generatePChkFake(
    amount: string,
    orderId: string,
    timestamp: string,
    hashKey: string
  ): Promise<string> {
    const cryptoApi = globalThis.crypto;

    if (!cryptoApi?.subtle) {
      throw new Error('현재 환경에서 결제 보안 해시를 계산할 수 없습니다.');
    }

    // 해시 입력 데이터 생성 (Node.js 예제와 동일)
    const dataString = amount + orderId + timestamp + hashKey;

    const encoder = new TextEncoder();
    const data = encoder.encode(dataString);
    const digest = await cryptoApi.subtle.digest('SHA-512', data);

    const hash = this.arrayBufferToBase64(digest);

    return hash;
  }

  /**
   * ArrayBuffer를 Base64 문자열로 변환
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';

    // 바이트 배열을 바이너리 문자열로 변환
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }

    if (typeof globalThis.btoa === 'function') {
      return globalThis.btoa(binary);
    }

    throw new Error('현재 환경에서 Base64 인코딩을 지원하지 않습니다.');
  }
}

/**
 * 싱글톤 인스턴스
 */
export const inicisPaymentRepository = new InicisPaymentRepositoryImpl();
